import { Audio } from '../../shared/audio';
import { ROUND_DURATIONS, type Difficulty, type FailMode, type RoundDuration } from '../../shared/config';
import { resetHighScores } from '../../shared/highScores';
import { getSettings, updateSettings, type Settings } from '../../shared/settings';
import { createDotsBg } from '../ui/decor';
import type { Screen, ScreenContext } from './types';

const RESET_CONFIRM_MS = 4000;
const RESET_CLEARED_MS = 1200;

interface SegmentOption<T> {
  readonly value: T;
  readonly label: string;
  readonly color: 'teal' | 'yellow' | 'orange' | 'purple';
}

function buildToggle(
  testid: string,
  labelText: string,
  icon: string,
  initial: boolean,
  onChange: (next: boolean) => void,
): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'settings-row settings-row-toggle';
  row.dataset.testid = `setting-${testid}`;

  const label = document.createElement('div');
  label.className = 'settings-row-label';
  const iconEl = document.createElement('span');
  iconEl.className = 'settings-row-icon';
  iconEl.textContent = icon;
  const labelText2 = document.createElement('span');
  labelText2.textContent = labelText;
  label.append(iconEl, labelText2);

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'settings-toggle';
  toggle.dataset.on = initial ? 'true' : 'false';
  toggle.dataset.testid = `toggle-${testid}`;
  const knob = document.createElement('div');
  knob.className = 'settings-toggle-knob';
  toggle.appendChild(knob);
  toggle.addEventListener('click', () => {
    const next = toggle.dataset.on !== 'true';
    toggle.dataset.on = next ? 'true' : 'false';
    onChange(next);
  });

  row.append(label, toggle);
  return row;
}

function buildSegmentedControl<T extends string | number>(
  testid: string,
  labelText: string,
  icon: string,
  options: ReadonlyArray<SegmentOption<T>>,
  initial: T,
  onChange: (next: T) => void,
): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'settings-row';
  row.dataset.testid = `setting-${testid}`;

  const label = document.createElement('div');
  label.className = 'settings-row-label';
  const iconEl = document.createElement('span');
  iconEl.className = 'settings-row-icon';
  iconEl.textContent = icon;
  const labelText2 = document.createElement('span');
  labelText2.textContent = labelText;
  label.append(iconEl, labelText2);
  row.appendChild(label);

  const segments = document.createElement('div');
  segments.className = 'settings-row-segments';

  const buttons: HTMLButtonElement[] = [];
  for (const opt of options) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'settings-segment';
    btn.dataset.color = opt.color;
    btn.dataset.active = opt.value === initial ? 'true' : 'false';
    btn.dataset.testid = `segment-${testid}-${String(opt.value)}`;
    btn.style.setProperty('--seg-color', `var(--${opt.color})`);
    btn.textContent = opt.label;
    btn.addEventListener('click', () => {
      for (const b of buttons) b.dataset.active = 'false';
      btn.dataset.active = 'true';
      onChange(opt.value);
    });
    buttons.push(btn);
    segments.appendChild(btn);
  }
  row.appendChild(segments);
  return row;
}

const DIFFICULTY_OPTIONS: ReadonlyArray<SegmentOption<Difficulty>> = [
  { value: 'easy', label: 'Easy', color: 'teal' },
  { value: 'normal', label: 'Normal', color: 'yellow' },
  { value: 'hard', label: 'Fast!', color: 'orange' },
];

const DURATION_LABELS: Readonly<Record<RoundDuration, string>> = {
  30: '30s',
  60: '60s',
  90: '90s',
  120: '2m',
};
const DURATION_COLORS: ReadonlyArray<SegmentOption<RoundDuration>['color']> = [
  'teal',
  'yellow',
  'orange',
  'purple',
];
const DURATION_OPTIONS: ReadonlyArray<SegmentOption<RoundDuration>> = ROUND_DURATIONS.map(
  (v, i) => ({
    value: v,
    label: DURATION_LABELS[v],
    color: DURATION_COLORS[i] ?? 'teal',
  }),
);

const FAIL_OPTIONS: ReadonlyArray<SegmentOption<FailMode>> = [
  { value: 'none', label: 'No worries', color: 'teal' },
  { value: 'points', label: '−Points', color: 'yellow' },
  { value: 'time', label: '−Time', color: 'orange' },
];

interface ResetRowHandle {
  row: HTMLDivElement;
  cleanup(): void;
}

function buildResetRow(
  testid: string,
  labelText: string,
  icon: string,
  idleLabel: string,
  confirmLabel: string,
  clearedLabel: string,
  onConfirm: () => void,
): ResetRowHandle {
  const row = document.createElement('div');
  row.className = 'settings-row settings-row-toggle';
  row.dataset.testid = `setting-${testid}`;

  const label = document.createElement('div');
  label.className = 'settings-row-label';
  const iconEl = document.createElement('span');
  iconEl.className = 'settings-row-icon';
  iconEl.textContent = icon;
  const labelTextEl = document.createElement('span');
  labelTextEl.textContent = labelText;
  label.append(iconEl, labelTextEl);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'settings-reset-btn chunky-btn yellow small';
  btn.dataset.testid = `reset-${testid}-btn`;
  btn.dataset.state = 'idle';
  btn.textContent = idleLabel;

  let timer: number | null = null;
  const clearTimer = (): void => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };
  const setIdle = (): void => {
    clearTimer();
    btn.dataset.state = 'idle';
    btn.classList.remove('orange');
    btn.classList.add('yellow');
    btn.textContent = idleLabel;
  };

  btn.addEventListener('click', () => {
    if (btn.dataset.state === 'idle') {
      btn.dataset.state = 'confirm';
      btn.classList.remove('yellow');
      btn.classList.add('orange');
      btn.textContent = confirmLabel;
      Audio.click();
      timer = window.setTimeout(setIdle, RESET_CONFIRM_MS);
    } else if (btn.dataset.state === 'confirm') {
      clearTimer();
      onConfirm();
      btn.dataset.state = 'cleared';
      btn.classList.remove('orange');
      btn.classList.add('yellow');
      btn.textContent = clearedLabel;
      Audio.click();
      timer = window.setTimeout(setIdle, RESET_CLEARED_MS);
    }
  });

  row.append(label, btn);
  return {
    row,
    cleanup(): void {
      clearTimer();
    },
  };
}

export function createSettingsScreen(): Screen {
  let container: HTMLDivElement | null = null;
  let resetHandle: ResetRowHandle | null = null;

  function mount(root: HTMLElement, ctx: ScreenContext): void {
    let current: Settings = getSettings();

    container = document.createElement('div');
    container.className = 'settings-screen';
    container.dataset.testid = 'settings-screen';
    container.appendChild(createDotsBg());

    const panel = document.createElement('div');
    panel.className = 'settings-panel';

    const title = document.createElement('h1');
    title.className = 'settings-title headline';
    title.textContent = 'Settings';
    panel.appendChild(title);

    const persist = (partial: Partial<Settings>): void => {
      current = updateSettings(partial);
      Audio.setSound(current.sound);
      Audio.setMusic(current.music);
    };

    panel.appendChild(
      buildToggle('sound', 'Sound Effects', '🔊', current.sound, (v) => persist({ sound: v })),
    );
    panel.appendChild(
      buildToggle('music', 'Music', '🎵', current.music, (v) => persist({ music: v })),
    );
    panel.appendChild(
      buildSegmentedControl<Difficulty>(
        'difficulty',
        'Difficulty',
        '🚀',
        DIFFICULTY_OPTIONS,
        current.difficulty,
        (v) => persist({ difficulty: v }),
      ),
    );
    panel.appendChild(
      buildSegmentedControl<RoundDuration>(
        'roundSeconds',
        'Round Length',
        '⏱️',
        DURATION_OPTIONS,
        current.roundSeconds,
        (v) => persist({ roundSeconds: v }),
      ),
    );
    panel.appendChild(
      buildSegmentedControl<FailMode>(
        'failMode',
        'Wrong Key / Miss',
        '🤷',
        FAIL_OPTIONS,
        current.failMode,
        (v) => persist({ failMode: v }),
      ),
    );

    resetHandle = buildResetRow(
      'highScores',
      'High Scores',
      '🏆',
      'Reset',
      'Tap again to confirm',
      'Cleared!',
      () => resetHighScores(),
    );
    panel.appendChild(resetHandle.row);

    const actions = document.createElement('div');
    actions.className = 'settings-actions';
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'chunky-btn teal';
    back.dataset.testid = 'back-btn';
    back.textContent = '← BACK TO MENU';
    back.addEventListener('click', () => ctx.goto('menu'));
    actions.appendChild(back);
    panel.appendChild(actions);

    container.appendChild(panel);
    root.appendChild(container);
  }

  function unmount(): void {
    resetHandle?.cleanup();
    resetHandle = null;
    container?.remove();
    container = null;
  }

  return { mount, unmount };
}
